import { useEffect, useRef } from "react";

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * True when the event landed on something the user is typing into, so a
 * single-character hotkey must not steal it.
 */
const isEditableTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element || typeof element.closest !== "function") return false;
  return (
    EDITABLE_TAGS.has(element.tagName) ||
    element.isContentEditable ||
    !!element.closest('[role="textbox"], [contenteditable="true"]')
  );
};

interface HotkeyOptions {
  enabled?: boolean;
}

/**
 * A document-level single-key shortcut that stays out of the way: it ignores
 * modifier combinations, IME composition, and any key press aimed at an
 * editable element.
 *
 * The handler owns `preventDefault()`. The hook cannot know whether the handler
 * will actually act — it may decline, e.g. because a modal already owns focus —
 * and swallowing the key either way would kill the browser's own default (and
 * any handler further down) for nothing.
 */
export function useHotkey(
  key: string,
  handler: (event: KeyboardEvent) => void,
  { enabled = true }: HotkeyOptions = {},
): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== key) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.isComposing) return;
      if (isEditableTarget(event.target)) return;

      handlerRef.current(event);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [key, enabled]);
}

export default useHotkey;
